import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { ArrowLeft, Check, Zap } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';

// Step 1: Basic info
const step1Schema = z.object({
  service_title: z.string().min(1, 'Service name is required').max(200),
  vantage_app_id: z.string().min(1, 'Vantage instance is required'),
  catalog_id: z.string().min(1, 'Data catalog is required'),
});

// Step 2: Matching config
const step2Schema = z.object({
  matchField: z.enum(['name', 'address']),
  fuzzyThreshold: z.number().min(0).max(100),
  fuzzyCaseSensitive: z.boolean(),
});

// Step 3: Catalog config
const step3Schema = z.object({
  catalogId: z.string().min(1, 'Catalog ID is required'),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;

// ABBYY app definition ID
const ABBYY_APP_DEFINITION_ID = '11db3456-0a78-43fc-9dba-e04bb95eb154';

// Default field mappings
const DEFAULT_FIELD_MAPPINGS: Record<string, string> = {
  'Vendor ID': 'vendor_id',
  'Vendor Name': 'name',
  'Vendor Address': 'street',
  'Vendor City': 'city',
  'Vendor Country': 'country',
  'Vendor Postal Code': 'postal_code',
  'Vendor Tax ID': 'tax_id',
  'Vendor Phone': 'phone',
  'Vendor Email': 'email',
  'Vendor Bank Account': 'bank_account',
};

export function SmartCorrectSetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);

  // Form state
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data>({
    matchField: 'name',
    fuzzyThreshold: 70,
    fuzzyCaseSensitive: false,
  });
  const [step3Data, setStep3Data] = useState<Step3Data | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({
    ...DEFAULT_FIELD_MAPPINGS,
  });

  // Fetch service definitions (Smart Correct type)
  const { data: definitionsData } = useQuery({
    queryKey: ['service-definitions'],
    queryFn: () =>
      api.get('/api/services/definition/list').then((r) => r.data),
  });

  // Fetch ABBYY Vantage app instances for Vantage instance selection
  const { data: vantageAppsData } = useQuery({
    queryKey: ['vantage-apps'],
    queryFn: () =>
      api.get('/api/apps/list', { params: { limit: 100 } }).then((r) => r.data),
    select: (data) => {
      const apps = data?.query?.data ?? data ?? [];
      return apps.filter((app: any) => app.app_definition_id === ABBYY_APP_DEFINITION_ID);
    },
  });

  // Find Smart Correct definitions (document_type contains 'suggest' or 'vendor')
  const definitions = (definitionsData?.query?.data ?? definitionsData ?? []).filter(
    (def: any) =>
      def.document_type?.includes('suggest') ||
      def.document_type?.includes('vendor') ||
      def.service_definition_title?.toLowerCase().includes('smart correct') ||
      def.service_definition_title?.toLowerCase().includes('vendor') ||
      def.service_definition_title?.toLowerCase().includes('suggest')
  );

  // If no Smart Correct-specific definitions, show all
  const availableDefinitions = definitions.length > 0 ? definitions : (definitionsData?.query?.data ?? definitionsData ?? []);

  const vantageApps = vantageAppsData ?? [];

  // Step 1 form
  const step1Form = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      service_title: '',
      vantage_app_id: '',
      catalog_id: '',
    },
  });

  // Fetch available data catalogs for the selected Vantage instance
  const selectedVantageAppId = step1Form.watch('vantage_app_id');
  const { data: catalogsData } = useQuery({
    queryKey: ['vantage-catalogs', selectedVantageAppId],
    queryFn: () =>
      api.get('/api/smart-correct/vantage-catalogs', { params: { app_instance_id: selectedVantageAppId } }).then((r) => r.data),
    enabled: !!selectedVantageAppId,
  });

  const catalogs = catalogsData ?? [];

  // Step 2 form
  const step2Form = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: step2Data,
  });

  // Step 3 form
  const step3Form = useForm<Step3Data>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      catalogId: '',
    },
  });

  // Create service mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/smart-correct/create', data),
    onSuccess: (response) => {
      toast.success('Smart Correct service created successfully');
      const serviceId =
        response.data?.query?.[0]?.service_instance_id ||
        response.data?.service_instance_id;
      if (serviceId) {
        navigate(`/smart-correct/${serviceId}`);
      } else {
        navigate('/smart-correct');
      }
      queryClient.invalidateQueries({ queryKey: ['smart-correct'] });
    },
    onError: () => toast.error('Failed to create Smart Correct service'),
  });

  function onStep1Submit(data: Step1Data) {
    setStep1Data(data);
    // Pre-populate Step 3 catalog from Step 1 selection
    step3Form.setValue('catalogId', data.catalog_id);
    setStep(2);
  }

  function onStep2Submit(data: Step2Data) {
    setStep2Data(data);
    setStep(3);
  }

  function onStep3Submit(data: Step3Data) {
    setStep3Data(data);
    setStep(4); // Review step
  }

  function handleCreate() {
    if (!step1Data) return;

    // Find the first Smart Correct definition, or let user pick
    const definitionId = availableDefinitions[0]?.service_definition_id;

    createMutation.mutate({
      service_definition_id: definitionId,
      vantage_app_id: step1Data.vantage_app_id,
      catalog_id: step1Data.catalog_id,
      service_title: step1Data.service_title,
      service_description: `Smart Correct: ${step2Data.matchField} matching (${step2Data.fuzzyThreshold}% threshold)`,
      config: {
        matching: {
          matchField: step2Data.matchField,
          fuzzyThreshold: step2Data.fuzzyThreshold,
          fuzzyCaseSensitive: step2Data.fuzzyCaseSensitive,
        },
        catalog: {
          catalogId: step3Data?.catalogId || step1Data.catalog_id,
          fields: fieldMappings,
        },
        fieldMapping: Object.fromEntries(
          Object.entries(fieldMappings).map(([vantage, internal]) => [
            vantage,
            internal,
          ])
        ),
      },
    });
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/smart-correct">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#cc0404]" />
            New Smart Correct
          </h1>
          <p className="text-gray-500 text-sm">
            Set up vendor auto-correction and matching
          </p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step
                  ? 'bg-[#cc0404] text-white'
                  : s === step
                  ? 'bg-[#cc0404] text-white ring-2 ring-[#cc0404]/30'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {s < step ? <Check className="h-4 w-4" /> : s}
            </div>
            <span
              className={`text-sm ${
                s <= step ? 'font-medium text-[#cc0404]' : 'text-gray-400'
              }`}
            >
              {s === 1
                ? 'Basic Info'
                : s === 2
                ? 'Matching'
                : s === 3
                ? 'Catalog'
                : 'Review'}
            </span>
            {s < 4 && <div className="w-8 h-px bg-gray-200 mx-2" />}
          </div>
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Name your Smart Correct service and select the Vantage instance and data catalog.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={step1Form.handleSubmit(onStep1Submit)}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="service_title">Service Name</Label>
                <Input
                  id="service_title"
                  placeholder="e.g., My Vendor Smart Correct"
                  {...step1Form.register('service_title')}
                />
                {step1Form.formState.errors.service_title && (
                  <p className="text-sm text-red-500">
                    {step1Form.formState.errors.service_title.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Vantage Instance</Label>
                <Select
                  onValueChange={(val) => {
                    step1Form.setValue('vantage_app_id', val);
                    step1Form.setValue('catalog_id', ''); // reset catalog when Vantage changes
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Vantage instance" />
                  </SelectTrigger>
                  <SelectContent>
                    {vantageApps.map((app: any) => (
                      <SelectItem
                        key={app.app_instance_id}
                        value={app.app_instance_id}
                      >
                        {app.app_instance_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {step1Form.formState.errors.vantage_app_id && (
                  <p className="text-sm text-red-500">
                    {step1Form.formState.errors.vantage_app_id.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Data Catalog</Label>
                {!selectedVantageAppId ? (
                  <p className="text-sm text-gray-400">Select a Vantage instance first</p>
                ) : catalogs.length === 0 ? (
                  <p className="text-sm text-gray-400">No catalogs available for this Vantage instance</p>
                ) : (
                  <Select
                    onValueChange={(val) => step1Form.setValue('catalog_id', val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select data catalog" />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogs.map((catalog: any) => (
                        <SelectItem
                          key={catalog.id}
                          value={catalog.id}
                        >
                          {catalog.name || catalog.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {step1Form.formState.errors.catalog_id && (
                  <p className="text-sm text-red-500">
                    {step1Form.formState.errors.catalog_id.message}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit">Next: Matching Config</Button>
                <Link to="/smart-correct">
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Matching Config */}
      {step === 2 && (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle>Matching Configuration</CardTitle>
            <CardDescription>
              Configure how Smart Correct matches vendors from OCR data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={step2Form.handleSubmit(onStep2Submit)}
              className="space-y-6"
            >
              <div className="space-y-2">
                <Label>Match Field</Label>
                <p className="text-sm text-gray-500">
                  Which field to use for fuzzy matching vendors.
                </p>
                <Select
                  defaultValue={step2Data.matchField}
                  onValueChange={(val) =>
                    step2Form.setValue('matchField', val as 'name' | 'address')
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Vendor Name</SelectItem>
                    <SelectItem value="address">Delivery Address</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fuzzy Search Threshold: {step2Form.watch('fuzzyThreshold')}%</Label>
                <p className="text-sm text-gray-500">
                  Minimum similarity score (0-100) to consider a match. Lower = more lenient, Higher = stricter.
                </p>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  {...step2Form.register('fuzzyThreshold', { valueAsNumber: true })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#cc0404]"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>0 (Match anything)</span>
                  <span>50 (Lenient)</span>
                  <span>70 (Default)</span>
                  <span>100 (Exact only)</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="fuzzyCaseSensitive"
                  {...step2Form.register('fuzzyCaseSensitive')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="fuzzyCaseSensitive" className="font-normal">
                  Case-sensitive matching
                </Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button type="submit">Next: Catalog Config</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Catalog & Field Mapping */}
      {step === 3 && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Catalog & Field Mapping</CardTitle>
            <CardDescription>
              Select the Vantage catalog and map fields between Vantage and Connectfy.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={step3Form.handleSubmit(onStep3Submit)}
              className="space-y-6"
            >
              <div className="space-y-2">
                <Label>Data Catalog</Label>
                <p className="text-sm text-gray-500">
                  The Vantage data catalog to search against for vendor matching.
                </p>
                {catalogs.length === 0 ? (
                  <p className="text-sm text-gray-400">No catalogs available — go back to Step 1 and select a Vantage instance first.</p>
                ) : (
                  <Select
                    defaultValue={step3Data?.catalogId || step1Data?.catalog_id || ''}
                    onValueChange={(val) =>
                      step3Form.setValue('catalogId', val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogs.map((catalog: any) => (
                        <SelectItem
                          key={catalog.id}
                          value={catalog.id}
                        >
                          {catalog.name || catalog.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <Separator />

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold">Field Mapping</h3>
                  <p className="text-sm text-gray-500">
                    Map Vantage extraction fields to internal vendor fields.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-sm font-medium text-gray-500">
                    Vantage Field
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    Internal Field
                  </div>
                  {Object.entries(fieldMappings).map(
                    ([vantageField, internalField]) => (
                      <div key={vantageField} className="col-span-2 grid grid-cols-2 gap-3">
                        <Input value={vantageField} disabled className="bg-gray-50" />
                        <Input
                          value={internalField}
                          onChange={(e) =>
                            setFieldMappings((prev) => ({
                              ...prev,
                              [vantageField]: e.target.value,
                            }))
                          }
                          placeholder="internal field name"
                        />
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                >
                  Back
                </Button>
                <Button type="submit">Next: Review</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Create */}
      {step === 4 && step1Data && (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Review Configuration</CardTitle>
            <CardDescription>
              Confirm your Smart Correct configuration before creating.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-2">Basic Info</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Service Name:</span>{' '}
                  <span className="font-medium">{step1Data.service_title}</span>
                </div>
                <div>
                  <span className="text-gray-500">Vantage Instance:</span>{' '}
                  <span className="font-medium">
                    {vantageApps.find(
                      (a: any) => a.app_instance_id === step1Data.vantage_app_id
                    )?.app_instance_name || step1Data.vantage_app_id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Data Catalog:</span>{' '}
                  <span className="font-medium">
                    {catalogs.find(
                      (c: any) => c.id === step1Data.catalog_id
                    )?.name || step1Data.catalog_id}
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-sm font-semibold mb-2">Matching Config</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Match Field:</span>{' '}
                  <span className="font-medium capitalize">
                    {step2Data.matchField}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Threshold:</span>{' '}
                  <span className="font-medium">
                    {step2Data.fuzzyThreshold}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Case Sensitive:</span>{' '}
                  <span className="font-medium">
                    {step2Data.fuzzyCaseSensitive ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-sm font-semibold mb-2">Catalog & Fields</h3>
              <div className="text-sm mb-3">
                <span className="text-gray-500">Catalog:</span>{' '}
                <span className="font-medium">
                  {catalogs.find(
                    (c: any) => c.id === (step3Data?.catalogId || step1Data?.catalog_id)
                  )?.name || step3Data?.catalogId || step1Data?.catalog_id}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="font-medium text-gray-500">Vantage → Internal</div>
                <div />
                {Object.entries(fieldMappings).map(
                  ([vantage, internal]) => (
                    <div key={vantage} className="col-span-2 grid grid-cols-2 gap-2">
                      <span>{vantage}</span>
                      <span className="font-mono text-[#cc0404]">{internal}</span>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(3)}
              >
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending
                  ? 'Creating...'
                  : 'Create Smart Correct'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}